import React, { useContext, useState } from 'react';
import { Check, Zap, Star, Sparkles, ArrowRight, Loader2 } from 'lucide-react';
import styles from './styles.module.css';
import { Context } from '../../context/UserContext';
import SubscriptionCheckoutModal from '../../componentes/SubscriptionModal';

// Mapa planId → planName do banco
const PLAN_NAME_MAP = {
    plus:    'Plano Plus - Mensal',
    premium: 'Plano Premium - Mensal',
};

const PLAN_ORDER = { free: 0, plus: 1, premium: 2 };

const planos = [
    {
        id:        'free',
        nome:      'Gratuito',
        preco:     'R$ 0,00',
        valor:     0,
        periodo:   '/mês',
        descricao: 'Comece sem custos e conheça nosso serviço.',
        icon:      Star,
        beneficios: [
            '1 nota de orçamento por semana',
            'Acesso limitado a funcionalidades',
        ],
    },
    {
        id:        'plus',
        nome:      'Plus',
        preco:     'R$ 12,90',
        valor:     12.90,
        periodo:   '/mês',
        descricao: 'Mais recursos para expandir sua experiência.',
        icon:      Zap,
        beneficios: [
            '5 notas de orçamento por semana',
            'Acesso a funcionalidades avançadas',
        ],
        destaque: true,
    },
    {
        id:        'premium',
        nome:      'Premium',
        preco:     'R$ 21,90',
        valor:     21.90,
        periodo:   '/mês',
        descricao: 'Todos os recursos e suporte premium.',
        icon:      Sparkles,
        beneficios: [
            'Notas de orçamento ilimitadas',
            'Acesso a todas as funcionalidades',
        ],
    },
];

export default function ChangePlan() {
    const { user, setUser, fetchUser } = useContext(Context);
    const [loadingPlan, setLoadingPlan]         = useState(null); // spinner do cancelamento p/ free
    const [confirmDowngrade, setConfirmDowngrade] = useState(null); // plano destino
    const [checkoutPlan, setCheckoutPlan]       = useState(null);   // plano pago aguardando checkout

    const planoAtual = user?.plan || 'free';
    const token      = JSON.parse(localStorage.getItem('token'));

    // Downgrade para free: cancela a assinatura atual, sem precisar de cartão.
    async function cancelarParaFree() {
        setLoadingPlan('free');
        setConfirmDowngrade(null);

        const subs = await fetch(
            `${process.env.REACT_APP_API}/subscriptions/my`,
            { headers: { Authorization: `Bearer ${token}` } }
        ).then(r => r.json()).catch(() => ({ subscriptions: [] }));

        const activeSub = (subs.subscriptions || []).find(
            s => s.status !== 'cancelled'
        );

        if (activeSub) {
            const res = await fetch(
                `${process.env.REACT_APP_API}/subscriptions/${activeSub._id}/cancel`,
                { method: 'PUT', headers: { Authorization: `Bearer ${token}` } }
            ).then(r => r.json());

            if (!res.error) {
                setUser(prev => ({ ...prev, plan: 'free', currentSubscription: null }));
                await fetchUser();
            }
        } else {
            setUser(prev => ({ ...prev, plan: 'free' }));
        }

        setLoadingPlan(null);
    }

    function handleSelectPlan(plano) {
        if (plano.id === planoAtual) return;

        const currentOrder = PLAN_ORDER[planoAtual] ?? 0;
        const targetOrder  = PLAN_ORDER[plano.id]   ?? 0;

        // Downgrade → pede confirmação (independente de ser p/ free ou p/ outro plano pago)
        if (targetOrder < currentOrder) {
            setConfirmDowngrade(plano);
            return;
        }

        // Upgrade → abre o checkout direto
        setCheckoutPlan(plano);
    }

    function handleConfirmDowngrade() {
        if (confirmDowngrade.id === 'free') {
            cancelarParaFree();
            return;
        }
        // Downgrade entre planos pagos (ex: Premium → Plus) também passa pelo Brick
        setCheckoutPlan(confirmDowngrade);
        setConfirmDowngrade(null);
    }

    function getLabelBtn(plano) {
        if (plano.id === planoAtual) return 'Plano atual';
        const currentOrder = PLAN_ORDER[planoAtual] ?? 0;
        const targetOrder  = PLAN_ORDER[plano.id]   ?? 0;
        if (targetOrder < currentOrder) return 'Fazer downgrade';
        if (plano.id === 'free')        return 'Usar gratuitamente';
        return 'Assinar agora';
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <span className={styles.badge}>Plano</span>
                <h2 className={styles.title}>Escolha seu plano</h2>
                <p className={styles.subtitle}>
                    Faça upgrade a qualquer momento e cancele quando quiser.
                </p>
            </div>

            <div className={styles.grid}>
                {planos.map(plano => {
                    const Icon    = plano.icon;
                    const isAtual = plano.id === planoAtual;
                    const isLoading = loadingPlan === plano.id;

                    return (
                        <div key={plano.id}
                            className={`${styles.card} ${plano.destaque ? styles.destaque : ''} ${isAtual ? styles.atual : ''}`}>

                            {plano.destaque && !isAtual && (
                                <div className={styles.popularTag}>Mais popular</div>
                            )}
                            {isAtual && (
                                <div className={styles.atualTag}>Seu plano atual</div>
                            )}

                            <div className={styles.cardTop}>
                                <div className={`${styles.iconBox} ${plano.destaque ? styles.iconBoxDestaque : ''}`}>
                                    <Icon size={20} />
                                </div>
                                <h3 className={styles.planName}>{plano.nome}</h3>
                                <p className={styles.planDesc}>{plano.descricao}</p>
                            </div>

                            <div className={styles.priceRow}>
                                <span className={styles.price}>{plano.preco}</span>
                                <span className={styles.periodo}>{plano.periodo}</span>
                            </div>

                            <ul className={styles.beneficios}>
                                {plano.beneficios.map((b, i) => (
                                    <li key={i} className={styles.beneficioItem}>
                                        <span className={`${styles.checkIcon} ${plano.destaque ? styles.checkDestaque : ''}`}>
                                            <Check size={13} strokeWidth={3} />
                                        </span>
                                        {b}
                                    </li>
                                ))}
                            </ul>

                            <button
                                className={`${styles.btn} ${plano.destaque ? styles.btnDestaque : ''} ${isAtual ? styles.btnAtual : ''}`}
                                onClick={() => handleSelectPlan(plano)}
                                disabled={isAtual || isLoading}
                            >
                                {isLoading
                                    ? <Loader2 size={16} className={styles.spinner} />
                                    : isAtual
                                        ? 'Plano atual'
                                        : <>{getLabelBtn(plano)} <ArrowRight size={15} /></>
                                }
                            </button>
                        </div>
                    );
                })}
            </div>

            <p className={styles.disclaimer}>
                Pagamentos processados com segurança pelo{' '}
                <strong>Mercado Pago</strong>. Você será redirecionado ao checkout
                ao clicar em "Assinar agora".
            </p>

            {/* ── Modal de confirmação de downgrade ── */}
            {confirmDowngrade && (
                <div className={styles.confirmOverlay}>
                    <div className={styles.confirmModal}>
                        <h3 className={styles.confirmTitle}>Fazer downgrade?</h3>
                        <p className={styles.confirmText}>
                            Seu plano será rebaixado para{' '}
                            <strong>{confirmDowngrade.nome}</strong>
                            {confirmDowngrade.id === 'free'
                                ? ' e a cobrança recorrente será cancelada imediatamente.'
                                : '.'}
                        </p>
                        <div className={styles.confirmActions}>
                            <button
                                className={styles.confirmYes}
                                onClick={handleConfirmDowngrade}
                                disabled={loadingPlan !== null}
                            >
                                {loadingPlan === confirmDowngrade.id
                                    ? <Loader2 size={15} className={styles.spinner} />
                                    : 'Sim, fazer downgrade'
                                }
                            </button>
                            <button
                                className={styles.confirmNo}
                                onClick={() => setConfirmDowngrade(null)}
                            >
                                Cancelar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Checkout (upgrade ou downgrade entre planos pagos) ── */}
            {checkoutPlan && (
                <SubscriptionCheckoutModal
                    plan={{
                        planName: PLAN_NAME_MAP[checkoutPlan.id],
                        name:     checkoutPlan.nome,
                        amount:   checkoutPlan.valor,
                    }}
                    payerEmail={user?.email}
                    onClose={() => setCheckoutPlan(null)}
                    onSuccess={async () => {
                        setCheckoutPlan(null);
                        await fetchUser();
                    }}
                />
            )}
        </div>
    );
}